import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import PageHeader from '../components/PageHeader';
import { PRIMARY } from '../components/CaptureSheets';
import { LOCALE } from '../lib/format';
import { vnDate } from '../lib/coach';
import { useApp } from '../lib/AppContext';

// Prices per seat until App Store purchases are wired up (with Pro, via RevenueCat). Placeholders the owner
// sets before launch (see docs/ke-hoach-sau-khi-co-tai-khoan-apple.md, section 4.2).
// Longer periods cost less per month. A seat (59.000 ₫/tháng) is below Pro's 79.000 ₫/tháng: coaches buy several.
const PERIODS = [
  { id: 'monthly', label: 'Theo tháng', per: 'tháng', months: 1, price: 59000 },
  { id: 'quarterly', label: '3 tháng', per: '3 tháng', months: 3, price: 169000 },
  { id: 'half_yearly', label: '6 tháng', per: '6 tháng', months: 6, price: 319000 },
  { id: 'yearly', label: 'Theo năm', per: 'năm', months: 12, price: 589000 },
];
const VOLUME = { from: 10, discount: 0.15 };
const vnd = (n) => `${Math.round(n).toLocaleString(LOCALE)} ₫`;
const periodOf = (id) => PERIODS.find((p) => p.id === id) || PERIODS[0];

const unitPrice = (period, seats) => periodOf(period).price * (seats >= VOLUME.from ? 1 - VOLUME.discount : 1);
// Against paying monthly for the same months.
const saving = (p) => Math.round((1 - p.price / (PERIODS[0].price * p.months)) * 100);

// ② "Gói huấn luyện viên": seats to gift Pro with, for 1, 3, 6 or 12 months. Built like the Pro sheet: the pay button
// only explains that payment through the App Store is coming; until then an admin grants seats.
export default function CoachPlanPage() {
  const { api } = useApp();
  const [plan, setPlan] = useState(undefined);
  const [seats, setSeats] = useState(3);
  const [period, setPeriod] = useState('monthly');
  const [note, setNote] = useState(null);
  const [offline, setOffline] = useState(null);
  // "Mua chỗ đầu tiên" (Người chưa dùng app, F6) comes here with ?seats=1.
  const [params] = useSearchParams();

  useEffect(() => {
    api.get('/coach').then((c) => {
      setPlan(c.plan);
      setOffline(c.offline);
      if (c.plan) {
        setSeats(c.plan.seats + 1);
        setPeriod(c.plan.period);
      } else if (Number(params.get('seats')) > 0) {
        setSeats(Number(params.get('seats')));
      }
    }).catch(() => setPlan(null));
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = plan?.active ? plan : null;
  const min = Math.max(1, current?.seats_used || 0);
  const total = unitPrice(period, seats) * seats;
  const added = current ? seats - current.seats : 0;
  // Adding seats mid-period pays for the days left; fewer seats apply from the next period.
  const daysLeft = current ? Math.max(0, Math.ceil((new Date(current.period_ends_at) - Date.now()) / 86400000)) : 0;
  const periodDays = period === 'yearly' ? 365 : periodOf(period).months * 30;
  const dueToday = current ? (added > 0 ? unitPrice(period, seats) * added * Math.min(1, daysLeft / periodDays) : 0) : total;
  const step = 'grid size-12 place-items-center rounded-[14px] border border-line bg-base-100 text-xl font-semibold disabled:opacity-40';

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Gói huấn luyện viên" fallback="/coach" />
      {current && (
        <section className="rounded-[20px] bg-base-100 p-4 text-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">Hiện có</p>
          <p className="text-lg font-bold">{current.seats} chỗ · {periodOf(current.period).label.toLowerCase()}</p>
          <p className="text-muted">Đang dùng {current.seats_used} chỗ · gia hạn ngày {vnDate(current.period_ends_at)}</p>
        </section>
      )}

      <section className="flex flex-col items-center gap-3 rounded-[24px] bg-base-100 p-5">
        <p className="text-sm text-muted">Số chỗ tặng Pro</p>
        <div className="flex items-center gap-5">
          <button type="button" className={step} aria-label="Bớt một chỗ" disabled={seats <= min} onClick={() => setSeats((s) => s - 1)}>−</button>
          <span className="tabular w-16 text-center text-5xl font-extrabold" aria-live="polite">{seats}</span>
          <button type="button" className={step} aria-label="Thêm một chỗ" disabled={seats >= 200} onClick={() => setSeats((s) => s + 1)}>+</button>
        </div>
        {current && added !== 0 && (
          <p className="text-sm font-semibold text-brand-text">{added > 0 ? `+${added}` : added} so với hiện tại</p>
        )}
        <p className="text-xs text-muted">Từ {VOLUME.from} chỗ: giảm {Math.round(VOLUME.discount * 100)}% mỗi chỗ</p>
        {offline && <p className="text-xs font-semibold text-brand-text">Theo dõi tối đa {offline.free + offline.per_seat * seats} người chưa dùng app</p>}
      </section>

      <div role="radiogroup" aria-label="Chu kỳ thanh toán" className="grid grid-cols-2 gap-2">
        {PERIODS.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={period === p.id} onClick={() => setPeriod(p.id)}
            className={`flex min-h-16 flex-col items-center justify-center rounded-[16px] border-2 px-3 py-2 ${period === p.id ? 'border-primary bg-[#e3f1e9]/50' : 'border-line bg-base-100'}`}>
            <span className="font-bold">{p.label}</span>
            <span className="text-xs text-brand-text">
              {p.months === 1 ? `${vnd(unitPrice(p.id, seats))}/chỗ` : `≈ ${vnd(Math.round(unitPrice(p.id, seats) / p.months / 100) * 100)}/chỗ/tháng · -${saving(p)}%`}
            </span>
          </button>
        ))}
      </div>

      <dl className="flex flex-col gap-2 rounded-[20px] bg-base-100 p-4 text-sm">
        <div className="flex justify-between"><dt>{seats} chỗ × {vnd(unitPrice(period, seats))}</dt><dd className="font-semibold">{vnd(total)}/{periodOf(period).per}</dd></div>
        {current && added > 0 && (
          <div className="flex justify-between text-muted"><dt>{added} chỗ thêm cho {daysLeft} ngày còn lại</dt><dd>{vnd(dueToday)}</dd></div>
        )}
        {current && added < 0 && <p className="text-muted">Bớt chỗ áp dụng từ kỳ sau.</p>}
        <div className="flex justify-between border-t border-line pt-2 text-base font-bold"><dt>Trả hôm nay</dt><dd>{vnd(dueToday)}</dd></div>
      </dl>

      <button type="button" className={PRIMARY} disabled={plan === undefined || (current && added === 0)}
        onClick={() => setNote('Thanh toán qua App Store sẽ có khi Macro Mate phát hành trên App Store.')}>
        {current ? `Cập nhật lên ${seats} chỗ` : `Mua ${seats} chỗ`}
      </button>
      {note && <p role="status" className="text-center text-sm text-muted">{note}</p>}
      <p className="text-center text-xs text-muted">Mỗi chỗ tặng Pro cho một học viên. Học viên tự tham gia bằng mã HLV không tốn chỗ.</p>
    </div>
  );
}
